import * as React from 'react';
import useSwitch from '@react-hook/switch';
import useMergedRef from '@react-hook/merged-ref';
import { useA11yButton } from '@accessible/button';
import clsx from 'clsx';
/**
 * A React hook for creating a headless a11y toggle button to the
 * W3C accessibility standard.
 *
 * @param target A React ref or HTML element
 * @param options Configuration options
 */

export function useA11yToggleButton(target, {
  active: controlledActive,
  defaultActive = false,
  onClick = noop,
  onChange = noop
} = {}) {
  const [active, toggle] = useSwitch(defaultActive, controlledActive, onChange);
  return Object.assign({
    'aria-pressed': active
  }, useA11yButton(target, event => {
    toggle();
    onClick(event);
  }));
}
/**
 * A component that turns its child element into a a11y toggle button
 * to the W3C accessiblity standard.
 */

export function ToggleButton({
  active,
  defaultActive = false,
  activeClass,
  inactiveClass,
  activeStyle,
  inactiveStyle,
  onChange = noop,
  children
}) {
  const ref = React.useRef(null);
  const props = children.props;
  const a11yProps = useA11yToggleButton(ref, {
    onChange,
    onClick: props.onClick,
    active,
    defaultActive
  });
  const pressed = a11yProps['aria-pressed'];
  const buttonStyle = pressed ? activeStyle : inactiveStyle;
  return /*#__PURE__*/React.cloneElement(children, {
    className: clsx(props.className, pressed ? activeClass : inactiveClass) || void 0,
    style: !props.style ? buttonStyle : Object.assign({}, props.style, buttonStyle),
    'aria-pressed': a11yProps['aria-pressed'],
    role: props.hasOwnProperty('role') ? props.role : a11yProps['role'],
    tabIndex: props.hasOwnProperty('tabIndex') ? props.tabIndex : a11yProps['tabIndex'],
    ref: useMergedRef(ref, // @ts-expect-error
    children.ref)
  });
}

function noop() {}

/* istanbul ignore next */
if (typeof process !== 'undefined' && process.env.NODE_ENV !== 'production') {
  ToggleButton.displayName = 'AccessibleToggleButton';
}